import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetUsitcSpiRatesBIZcoreTD,
  WipeUsitcSpiRatesBIZcoreTD,
  FetchUsitcSpiRatesBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import UsitcSpiRatesModal from './UsitcSpiRatesModal';

import global from "../../../../../global.scss";

const UsitcSpiRatesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [spiRatesList, setSpiRatesList] = useState([]);
  const [spiRatesTotal, setSpiRatesTotal] = useState(0);

  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "USITC HTS id", sort: "ref_hts_code_id" },
    { label: "SPI code", sort: "spi_code" },
    { label: "Preferential rate", sort: "preferential_rate" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetSpiRates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setSpiRatesList(state.usitcSpiRatesList.records || []);
      setSpiRatesTotal(state.usitcSpiRatesList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetSpiRates(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetUsitcSpiRatesBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeUsitcSpiRatesBIZcoreTD(setWiping)).then(() => {
      handleGetSpiRates();
    });
  };

  const handleFetch = () => {
    setFetchConfirmOpen(false);
    dispatch(FetchUsitcSpiRatesBIZcoreTD(null, setFetching)).then(() => {
      handleGetSpiRates();
    });
  };

  return (
    <Box>

      <UsitcSpiRatesModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetSpiRates}
      />

      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe USITC SPI Rates Table"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <ConfirmationModal
        open={fetchConfirmOpen}
        setOpen={setFetchConfirmOpen}
        title={"Fetch USITC SPI Rates from USITC"}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={handleFetch}
      />

      <TopBar
        title={"USITC SPI Rates"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button3Text={wiping ? "Wiping..." : "Wipe table"}
        button3OnClick={() => setWipeConfirmOpen(true)}
        button2Text={fetching ? "Fetching..." : "Fetch data"}
        button2OnClick={() => setFetchConfirmOpen(true)}
        button1Text={"Add SPI rate"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add USITC SPI rate");
          setModalOpen(!modalOpen);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          spiRatesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No USITC SPI rate found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                spiRatesList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.ref_hts_code_id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.spi_code || <NA />}</TableCell>
                    <TableCell>{row.preferential_rate || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps createdAt={row.created_at} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.spi_code || row.id || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={spiRatesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit SPI rate");
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default UsitcSpiRatesPage;
